import { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useCountry from 'hooks/useCountry';

import Grid from '@material-ui/core/Grid';
import Link from '@material-ui/core/Link';
import Typography from 'components/CncUI/Typography';
import Checkbox from 'components/CncUI/Checkbox';
import Table from 'components/CncUI/Table';

import EnumHelper from 'utilities/enumHelper';

const useStyles = makeStyles(({ palette }) => ({
  link: {
    color: palette.mycc.link,
  },
  header: {
    marginBottom: 0,
  },
  enabled: {
    color: palette.cncUI.default.primary,
  },
  empty: {
    '& .cncui-table-cell': {
      textAlign: 'center',
      height: '5em',
      fontStyle: 'italic',
      color: palette.cncUI.default.primary,
    },
  },
}));

const ComplexPanel = props => {
  const classes = useStyles();
  const { country } = useCountry();
  const isCanada = country === 'CAN';
  const { directDeposits, mappings } = props;
  const { isPaperLess = true } = (directDeposits && directDeposits[0]) || '';
  const [paperless, setPaperless] = useState(isPaperLess);
  const hasDirectDeposits = directDeposits && directDeposits.length > 0;

  const key = prop => `ComplexPanel-${prop}`;

  useEffect(() => {
    setPaperless(
      directDeposits &&
        directDeposits.length > 0 &&
        directDeposits[0].isPaperLess,
    );
  }, [directDeposits]);

  const header = (
    <Table.Head>
      <Table.Row>
        {['Bank Name', 'Account Type', 'Routing Number', 'Account Number']
          .filter(Boolean)
          .map(name => (
            <Table.Cell key={key(`TableHeader-${name}`)}>{name}</Table.Cell>
          ))}
      </Table.Row>
    </Table.Head>
  );

  const rows = directDeposits.map((item, index) => {
    item.isPaperless && setPaperless(true);
    return (
      <Table.Row key={key(`TableRow-${index}`)}>
        <Table.Cell>{item.bankName || ''}</Table.Cell>
        <Table.Cell>
          {EnumHelper.valueToOptionLabel(
            mappings.bankAccountTypes,
            item.bankAccountType,
          ).replace(' Account', '')}
        </Table.Cell>
        <Table.Cell>{item.routingNumber}</Table.Cell>
        <Table.Cell>{item.bankAccountNumber}</Table.Cell>
      </Table.Row>
    );
  });

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography
          variant="h5"
          className={clsx(classes.header, hasDirectDeposits && classes.enabled)}
        >
          Direct Deposit is {hasDirectDeposits ? 'ON' : 'OFF'}
        </Typography>
      </Grid>
      {!hasDirectDeposits && (
        <Grid item xs={12}>
          <Typography variant="body2" size="short">
            {`You will receive paper ${isCanada ? 'cheques' : 'checks'}.`}
          </Typography>
        </Grid>
      )}
      {hasDirectDeposits && (
        <Grid item xs={12}>
          <Typography variant="body2" size="long">
            {hasDirectDeposits &&
              'Your direct deposit accounts shown below are currently active. '}
            We improved the security and online process for setting up direct
            deposit which no longer supports your existing configuration.
            <br />
            <br />
            To make changes to your direct deposit accounts, please call our
            Employee Help Desk at{' '}
            <Link href="tel:8885704650" className={classes.link}>
              888-570-4650
            </Link>
            , or for Media Services call{' '}
            <Link href="tel:3102541702" className={classes.link}>
              310-254-1702
            </Link>
          </Typography>
        </Grid>
      )}
      {hasDirectDeposits && (
        <Grid item xs={12}>
          <Typography variant="h5">Your direct deposit accounts</Typography>
        </Grid>
      )}
      {hasDirectDeposits && (
        <Grid item xs={12}>
          <Table>
            {header}
            <Table.Body>
              {(rows || []).length ? (
                rows
              ) : (
                <Table.Row className={classes.empty}>
                  <Table.Cell colSpan={5}>No bank account added yet</Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Grid>
      )}
      {hasDirectDeposits && !isCanada && (
        <Grid item xs={12}>
          <Checkbox disabled checked={paperless}>
            Go paperless to view your pay stubs online.
          </Checkbox>
        </Grid>
      )}
    </Grid>
  );
};

export default ComplexPanel;
